import BackendService from 'BackendService';
import { Box } from '@mui/material';
import React, { useState } from 'react';
import FundOpportunity from '../../../models/FundOpportunity';
import {
  GenericError,
  MissingFieldsError,
  MissingContentError,
  PromotionPrerequisitesError,
  PromotionPrerequisiteIssue,
  StructuredError,
} from '../../../services/BaseBackendService';
import { StyledButton } from '../../styles/StyledButtons';
import StyledModal from '../../styles/StyledModal';
import FundStatusStepper from './FundStatusStepper';
import { StyledTypography } from '../../styles/styles';
import { shortPromotionIssueLabel } from './promotionPrerequisiteMessages';

interface StatusStepperProps {
  fundOpportunity: FundOpportunity;
  data?: {
    opportunity: FundOpportunity;
    investors: any[];
  };
  onDataLoad: () => void;
  onShowToast: (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;
  onFieldErrors: (errors: { [key: string]: string }) => void;
}

const StatusStepper: React.FC<StatusStepperProps> = ({
  fundOpportunity,
  data,
  onDataLoad,
  onShowToast,
  onFieldErrors,
}) => {
  const [prerequisiteModalOpen, setPrerequisiteModalOpen] = useState(false);
  const [prerequisiteIssues, setPrerequisiteIssues] = useState<PromotionPrerequisiteIssue[]>([]);
  const [prerequisiteTargetStatus, setPrerequisiteTargetStatus] = useState<
    FundOpportunity['status'] | null
  >(null);

  const STATUSES: FundOpportunity['status'][] = [
    'NEW',
    'APPROVED',
    'EOI',
    'APPLICATION_OPEN',
    'UNDER_MANAGEMENT',
    'COMPLETE',
  ];

  const LABELS: Partial<Record<FundOpportunity['status'], string>> = {
    NEW: 'New',
    APPROVED: 'Approved',
    EOI: 'EOI',
    APPLICATION_OPEN: 'Applications Open',
    UNDER_MANAGEMENT: 'Under Management',
    COMPLETE: 'Complete',
  };

  if (!data?.opportunity) {
    return null;
  }

  const overridesFromIssues = (
    issues: PromotionPrerequisiteIssue[],
  ): {
    overridePDF?: boolean;
    overrideMissingIrrUpdate?: boolean;
    overrideMissingSummaryImage?: boolean;
    overrideMissingCommittedInvestments?: boolean;
  } => {
    const o: {
      overridePDF?: boolean;
      overrideMissingIrrUpdate?: boolean;
      overrideMissingSummaryImage?: boolean;
      overrideMissingCommittedInvestments?: boolean;
    } = {};
    for (const row of issues) {
      if (row.overrideRequestParam === 'overridePDF') {
        o.overridePDF = true;
      }
      if (row.overrideRequestParam === 'overrideMissingIrrUpdate') {
        o.overrideMissingIrrUpdate = true;
      }
      if (row.overrideRequestParam === 'overrideMissingSummaryImage') {
        o.overrideMissingSummaryImage = true;
      }
      if (row.overrideRequestParam === 'overrideMissingCommittedInvestments') {
        o.overrideMissingCommittedInvestments = true;
      }
    }
    return o;
  };

  const handlePromote = (
    toStatus: FundOpportunity['status'],
    options?: {
      overridePDF?: boolean;
      overrideMissingIrrUpdate?: boolean;
      overrideMissingSummaryImage?: boolean;
      overrideMissingCommittedInvestments?: boolean;
    },
  ) => {
    const service = new BackendService();
    const id = fundOpportunity.id;
    const overridePDF = options?.overridePDF ?? false;
    const overrideMissingIrrUpdate = options?.overrideMissingIrrUpdate ?? false;
    const overrideMissingSummaryImage = options?.overrideMissingSummaryImage ?? false;
    const overrideMissingCommittedInvestments =
      options?.overrideMissingCommittedInvestments ?? false;

    let promise: Promise<void>;
    if (toStatus === 'APPROVED') {
      promise = service.approveFundOpportunity(id);
    } else if (toStatus === 'EOI') {
      promise = service.promoteFundOpportunityToEOI(id, {
        overridePDF,
        overrideMissingIrrUpdate,
        overrideMissingSummaryImage,
      });
    } else if (toStatus === 'APPLICATION_OPEN') {
      promise = service.promoteFundOpportunityToApplicationOpen(id);
    } else if (toStatus === 'UNDER_MANAGEMENT') {
      promise = service.promoteFundOpportunityToUnderManagement(
        id,
        overrideMissingCommittedInvestments,
      );
    } else if (toStatus === 'COMPLETE') {
      promise = service.promoteFundOpportunityToComplete(id);
    } else {
      return;
    }

    promise
      .then(() => {
        setPrerequisiteModalOpen(false);
        setPrerequisiteIssues([]);
        setPrerequisiteTargetStatus(null);
        onDataLoad();
        onShowToast('Fund opportunity promoted successfully.', 'success');
      })
      .catch((error: StructuredError) => {
        let toastMessage = 'Failed to promote fund opportunity.';

        if ((error as MissingFieldsError).missingFields) {
          const errorFields: { [key: string]: string } = {};
          const missingFields = (error as MissingFieldsError).missingFields;

          missingFields.forEach((field: string) => {
            if (field === 'addresses') {
              errorFields[field] = 'At least one address is required for this fund opportunity';
            } else {
              errorFields[field] = 'This field is required';
            }
          });

          onFieldErrors(errorFields);

          if (missingFields.includes('addresses')) {
            toastMessage =
              'An address is required to promote this fund opportunity. Please add at least one address below.';
          } else {
            toastMessage = 'Please fill in all required fields (see below)';
          }
        } else if ((error as PromotionPrerequisitesError).code === 'PROMOTION_PREREQUISITES') {
          const pe = error as PromotionPrerequisitesError;
          const blocking = pe.issues.filter((i) => !i.overridable);
          if (blocking.length > 0) {
            onShowToast(blocking.map((i) => shortPromotionIssueLabel(i.issue)).join(' '), 'error');
            return;
          }
          const overridable = pe.issues.filter((i) => i.overridable);
          if (overridable.length > 0) {
            setPrerequisiteIssues(overridable);
            setPrerequisiteTargetStatus(toStatus);
            setPrerequisiteModalOpen(true);
            return;
          }
        } else if ((error as MissingContentError).code === 'MISSING_CONTENT') {
          toastMessage =
            'Cannot promote to EOI: No published content found. Please publish content first.';
        } else if ((error as GenericError).error) {
          toastMessage = (error as GenericError).error;
        }

        onShowToast(toastMessage, 'error');
      });
  };

  const handlePrerequisiteConfirm = () => {
    if (!prerequisiteTargetStatus || prerequisiteIssues.length === 0) {
      return;
    }
    handlePromote(prerequisiteTargetStatus, overridesFromIssues(prerequisiteIssues));
  };

  const handlePrerequisiteCancel = () => {
    setPrerequisiteModalOpen(false);
    setPrerequisiteIssues([]);
    setPrerequisiteTargetStatus(null);
  };

  return (
    <>
      <FundStatusStepper<FundOpportunity['status']>
        key={`status-${data?.opportunity?.id}-${data?.opportunity?.status}`}
        statuses={STATUSES}
        activeStatus={data?.opportunity?.status}
        labelMap={LABELS}
        canPromote={true}
        onPromote={(status) => handlePromote(status)}
      />

      <StyledModal
        open={prerequisiteModalOpen}
        onClose={handlePrerequisiteCancel}
        title="Promotion prerequisites"
        tone="tone1"
        type="warning"
        maxWidth="sm"
        fullWidth
        actions={
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <StyledButton
              variant="outlined"
              buttonVariant="inactiveOrCancel"
              onClick={handlePrerequisiteCancel}
            >
              Cancel
            </StyledButton>
            <StyledButton variant="contained" onClick={handlePrerequisiteConfirm}>
              Proceed anyway
            </StyledButton>
          </Box>
        }
      >
        <StyledTypography tone="tone1" sx={{ mb: 2, fontSize: '1rem' }}>
          The following should be addressed before promoting. You can still proceed, but this is not
          recommended.
        </StyledTypography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, my: 0 }}>
          {prerequisiteIssues.map((row) => (
            <Box component="li" key={`${row.issue}-${row.overrideRequestParam ?? ''}`}>
              <StyledTypography tone="tone1" sx={{ fontSize: '1rem' }}>
                {shortPromotionIssueLabel(row.issue)}
              </StyledTypography>
            </Box>
          ))}
        </Box>
      </StyledModal>
    </>
  );
};

export default StatusStepper;
